<template>
  <input
    type="text"
    inputmode="decimal"
    :value="displayValue"
    v-bind="$attrs"
    @input="onInput"
    @blur="onBlur"
    @focus="onFocus"
    @keydown.enter.prevent="onEnter"
    @paste="onPaste"
  />
</template>

<script>
import { ref, watch } from 'vue'

// 安全算式求值：仅允许数字、运算符、括号、空格、科学计数法
const SAFE_CHARS = /^[0-9+\-*/().\seE]+$/
const TWO_NUM_RE = /-?\d+(?:\.\d+)?/g

function evaluate(expr) {
  const s = expr.trim()
  if (!s) return null
  if (!SAFE_CHARS.test(s)) return null
  if (!/\d/.test(s)) return null
  try {
    const result = new Function('return (' + s + ')')()
    if (typeof result === 'number' && isFinite(result)) return result
  } catch (e) { /* 无效表达式 */ }
  return null
}

export default {
  name: 'NumberInput',
  props: {
    modelValue: { type: [Number, String], default: 0 },
  },
  emits: ['update:modelValue', 'change', 'paste-pair', 'enter'],
  setup(props, { emit }) {
    const displayValue = ref(String(props.modelValue ?? 0))
    let lastEmittedValue = props.modelValue

    // 外部更新时同步显示值
    watch(() => props.modelValue, (newVal) => {
      displayValue.value = String(newVal ?? 0)
      lastEmittedValue = newVal
    })

    function commit(val) {
      if (val === lastEmittedValue) return
      lastEmittedValue = val
      displayValue.value = String(val)
      emit('update:modelValue', val)
      emit('change', val)
    }

    function evaluateAndCommit() {
      const raw = displayValue.value.trim()
      // 先尝试作为普通数字解析
      const n = Number(raw)
      if (raw !== '' && !isNaN(n) && isFinite(n)) {
        commit(n)
        return
      }
      // 再尝试算式求值
      const result = evaluate(raw)
      if (result !== null) {
        commit(result)
      } else if (raw === '' || raw === '-') {
        commit(0)
      } else {
        // 无效输入，回退到上次有效值
        displayValue.value = String(lastEmittedValue ?? 0)
      }
    }

    function onInput(e) {
      displayValue.value = e.target.value
    }

    function onFocus(e) {
      e.target.select()
    }

    function onBlur() {
      evaluateAndCommit()
    }

    function onEnter() {
      evaluateAndCommit()
      emit('enter')
    }

    function onPaste(e) {
      const text = (e.clipboardData || window.clipboardData).getData('text').trim()
      if (!text) return

      const matches = text.match(TWO_NUM_RE)
      if (!matches || matches.length !== 2) return

      e.preventDefault()
      const [a, b] = matches.map(Number)
      // 不调 commit，让父组件通过 paste-pair 统一设置两个字段
      // 父组件 handler 更新 ref → watch 触发 → displayValue 自动同步
      emit('paste-pair', [a, b])
    }

    return {
      displayValue,
      onInput, onBlur, onFocus, onEnter, onPaste,
    }
  },
}
</script>
